import BaseHeadBanner from "../../../components/elements/BaseHeadBanner"
import BasePage from "../../../components/layout/BasePage"
import { getDataFromSessionStorage, useDelayWhenSearchWithPagination, useResetPageOnFilterChange } from "../../../services/functions"
import { useMemo, useState, useContext, useEffect } from "react"
import { GeneralQueryKeys, LocalStorageKeys } from "../../../api/constants"
import { getPaginatedData } from "../../api/apiCalls"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import AbsenceTable from "../components/AbsenceTable"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import SearchbarAndNavigationButtons from "../../Buchhaltung/components/SearchbarAndNavigationButtons"
import { Pagination } from "antd"
import { useSolveConflictAbsence, useUpdateAbsence } from "../api/services"
import { AbsenceStatus, AbsenceType } from "../types"
import AbsenceModal from "../components/AbsenceModal"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { ReactComponent as PLUSINCIRCLE } from "../../../assets/PlusInCircleIcon.svg"
import AddAbsenceModal from "../components/AddAbsenceModal"
import BaseFilterModal from "../../../components/elements/BaseFilterModal"
import { SelfUserContext } from "../../../components/contexts/SelfUserContext"
import { Employee } from "../../Mitarbeiter/types/Types"
import PersonalAbsenceValues from "../components/PersonalAbsenceValues"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import { P } from "../../../auth/permissions"
import { Authorized } from "../../../components/authentication/Authorized"
import { useAbility } from "../../../components/contexts/AbilityContext"

export default function AbsencePage() {
    const [showFilter, setshowFilter] = useState(false)
    const [invertSort, setinvertSort] = useState(getDataFromSessionStorage(LocalStorageKeys.ABSENCE_FILTER + "invertSort"))
    const [searchTermAbsence, setsearchTermAbsence] = useState(getDataFromSessionStorage(LocalStorageKeys.ABSENCE_FILTER + "searchTerm"))
    const [filterDateSince, setfilterDateSince] = useState(getDataFromSessionStorage(LocalStorageKeys.ABSENCE_FILTER + "startDate"))
    const [filterDateTo, setfilterDateTo] = useState(getDataFromSessionStorage(LocalStorageKeys.ABSENCE_FILTER + "endDate"))
    const [page, setpage] = useState(getDataFromSessionStorage(LocalStorageKeys.ABSENCE_FILTER + "page") ?? 1)
    const handlePageChange = (page: number) => {
        sessionStorage.setItem(LocalStorageKeys.ABSENCE_FILTER + "page", JSON.stringify(page))
        setpage(page)
    }
    const searchParamsAbsence = useDelayWhenSearchWithPagination(searchTermAbsence, LocalStorageKeys.ABSENCE_FILTER + "searchTerm")
    useResetPageOnFilterChange([searchParamsAbsence, filterDateSince, filterDateTo, invertSort], () => handlePageChange(1))
    const queryKeyAbsence = GeneralQueryKeys.ABSENCE + searchParamsAbsence + filterDateSince + filterDateTo + invertSort + showFilter + page
    const { data, error, isLoading } = useQuery({
        queryKey: [queryKeyAbsence],
        queryFn: () =>
            getPaginatedData({
                pageType: "absence",
                page: page,
                limit: 20,
                search: searchParamsAbsence,
                invertSort: invertSort,
                startDate: filterDateSince,
                endDate: filterDateTo,
            }),
    })
    const displayedAbsences = useMemo(() => data?.docs, [data])

    // UPDATE ABSENCE
    const queryclient = useQueryClient()
    const [selectedAbsence, setSelectedAbsence] = useState<AbsenceType>()
    const { mutate: updateAbsence } = useUpdateAbsence(queryclient, queryKeyAbsence)
    const handleUpdateAbsence = (id: string, body: AbsenceType) => {
        modalCon?.trigger(1)
        updateAbsence({ id: id, body: body })
        setSelectedAbsence(undefined)
    }

    // solve conflict absences
    const { mutate: solveConflictAbsence } = useSolveConflictAbsence(queryclient, queryKeyAbsence)
    const handleSolveConflictAbsence = (parentId: string, conflictId: string) => {
        solveConflictAbsence({ parentId: parentId, conflictId: conflictId })
    }

    // FRONTEN LOGIC
    const ability = useAbility()
    const userIsEmployee = !ability.can(P.abwesenheiten_read_write.action, P.abwesenheiten_read_write.subject)
    const userSelf = useContext(SelfUserContext)
    const modalCon = useContext(ModalContext)
    useEffect(() => {
        if (displayedAbsences && selectedAbsence)
            setSelectedAbsence(displayedAbsences.find((absence: AbsenceType) => absence._id === selectedAbsence._id))
    }, [displayedAbsences])

    return (
        <BasePage>
            <BaseHeadBanner
                title="Abwesenheiten"
                button={
                    <Authorized allOf={[P.abwesenheiten_create]}>
                        <PLUSINCIRCLE className="w-8 h-8 cursor-pointer hidden md:block" onClick={() => modalCon?.trigger(0)} />
                    </Authorized>
                }
            />
            <Authorized allOf={[P.abwesenheiten_read_write]}>
                <SearchbarAndNavigationButtons
                    searchBarPlaceholder="Nach Typ / Status / Mitarbeiter suchen..."
                    searchTerm={searchTermAbsence}
                    setsearchTerm={setsearchTermAbsence}
                    sortDirection={invertSort ?? false}
                    sort={() => setinvertSort(!invertSort)}
                    showFilter={() => setshowFilter(!showFilter)}
                />
            </Authorized>
            <BaseGrowBody>
                {isLoading && <Loading />}
                {error && <ErrorPage />}
                {data && !isLoading && !error && (
                    <AbsenceTable
                        absence={displayedAbsences}
                        handleUpdateAbsence={handleUpdateAbsence}
                        openAbsenceModal={(item: AbsenceType) => {
                            setSelectedAbsence(item)
                            modalCon?.trigger(1)
                        }}
                        userIsEmployee={userIsEmployee}
                    />
                )}
            </BaseGrowBody>
            {/* FILTER */}
            <BaseFilterModal
                visible={showFilter}
                setVisible={() => setshowFilter(!showFilter)}
                statusType="absence"
                searchdateSince={filterDateSince}
                changeDateSince={setfilterDateSince}
                searchdateTo={filterDateTo}
                changeDateTo={setfilterDateTo}
                removeFilter={() => {
                    setfilterDateSince("")
                    setfilterDateTo("")
                }}
                sessionStorageKey={LocalStorageKeys.ABSENCE_FILTER}
            />
            {/* PAGINATION */}
            <div className="flex justify-center w-full p-4">
                <Pagination
                    pageSize={20}
                    defaultCurrent={1}
                    current={page}
                    onChange={handlePageChange}
                    total={data?.totalDocs}
                    showSizeChanger={false}
                />
            </div>
            {userIsEmployee && <PersonalAbsenceValues userSelf={userSelf?.user as Employee} displayedAbsences={displayedAbsences as AbsenceType[]} />}
            <AddAbsenceModal
                modalId={0}
                closeModal={() => modalCon?.trigger(0)}
                queryKey={queryKeyAbsence}
                userIsEmployee={userIsEmployee}
                userSelf={userSelf?.user as Employee}
            />
            <AbsenceModal
                modalId={1}
                absence={selectedAbsence as AbsenceType}
                handleSolveConflictAbsence={handleSolveConflictAbsence}
                handleRejectAbsence={(id) => handleUpdateAbsence(id, { status: AbsenceStatus.REJECTED } as AbsenceType)}
                handleApproveAbsence={(id) => handleUpdateAbsence(id, { status: AbsenceStatus.APPROVED } as AbsenceType)}
                handleCancelAbsence={(id) => handleUpdateAbsence(id, { status: AbsenceStatus.CANCELLED } as AbsenceType)}
            />
        </BasePage>
    )
}
